import React, { useState, useEffect, useRef } from 'react'
import { Button, Form } from 'antd'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import { store as reduxStore } from 'index'
import { setPagination, useIsMobile, formatBytes } from 'utils'
import { getPlanFileSizeLimit, hasPlanOversizedFile } from 'utils/plan'
import { reloadPlanLicense } from 'services/plan/reloadLicense'
import sanitizeHtml from 'utils/sanitize'
import { getTempEnvParam } from 'env'
import { useSelector } from 'react-redux'
import { SERVICE_NAME } from 'service'
import { getTotpSecretLength, SHORT_TOTP_SECRET_LEN } from 'services/totp'
import { totpFingerprint, hasFingerprint } from 'services/totp/duplicateRegistry'
import {
  confirmDelete,
  confirmDiscard,
  errorTranslationMessage,
  ArrowLeftIcon,
  confirmBase,
} from 'utils/ui'
import {
  getProcessEmailList,
  EMAIL_REGEX,
  sendDarkWebService,
  checkLeakedRecord,
  getZdwExpiredAt,
} from 'dUtils'
import ResourceService from 'api/Resource'
import { FieldManager, SRC_TYPE } from 'pages/records/components/helper'
import { featureFlags } from 'config/feature-flags'
import { uploadFilesToS3, mapUploadResultsToSendData } from 'utils/importExport/utils/s3Upload'
import { guardUnloadDuringUpload } from 'services/attachmentV2/uploadManager'
// 순차 큐를 별도 leaf 모듈로 둔 이유는 그 파일 상단 주석 참조 (호출처 테스트 가능성)
import { runChunkedUploadsSequentially } from 'services/attachmentV2/uploadQueue'

import styles from 'pages/records/components/CardView/style.module.scss'
import closeIcon from '@/assets/images/icon-close-24.svg'

import CardView from './CardView'

const disableCreateBtn = (value) => {
  reduxStore.dispatch({
    type: 'menu/SET_STATE',
    payload: {
      disableCreate: value,
    },
  })
}

// 신규 레코드는 저장 전이라 서버에 행이 없는데 멀티파트 API 는 경로에 resource_id 를 요구한다.
// initiate 는 object_key 의 워크스페이스·resource_id 일치만 검증하고 행 존재를 요구하지 않고
// (services/attachment_multipart_service.py:79-80), createPresigned 는 클라이언트가 지정한
// resource_id 를 받아 중복만 검사한다(api/resource_presigned.py:157,161) → 클라이언트 선발급이 성립.
const generateResourceId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  // randomUUID 는 secure context 전용이라 http 로 띄운 로컬 환경에서 없을 수 있다.
  // 여기서 만든 값은 서버에서 중복 검사를 거치므로 충돌은 저장 실패로 드러난다(조용한 덮어쓰기 아님).
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}`
}

// 청크 업로드 실패 코드 → 사용자 문구. 취소(ABORTED)는 사용자가 의도한 동작이라 에러로 알리지 않는다.
const CHUNK_UPLOAD_ERROR_CODES = new Set([
  'PART_UPLOAD_FAILED',
  'PART_UPLOAD_STALLED',
  'PART_COUNT_MISMATCH',
  'ETAG_UNAVAILABLE',
  'ENCRYPT_FAILED',
  'INIT_FAILED',
  'UNSAFE_CHUNK_SIZE',
  'ABORTED',
  // 멀티파트 가드(services/attachmentV2/multipartGuard.js)가 던지는 코드. 이 집합에 없으면
  // errorTranslationMessage 로 떨어지고, 그 함수는 `workspace.error.{code}` 를 폴백 없이
  // 그대로 노출한다(utils/ui/index.js:1521-1527) → 키가 없어 토스트에 raw key 가 뜬다.
  // (ko/en 양쪽에 없고 i18n 에 missing key 핸들러도 없음 — src/i18n.js:33-34)
  'MISSING_RESOURCE_ID',
])

const chunkUploadErrorText = (code, t) => {
  if (code === 'ENCRYPT_FAILED' || code === 'INIT_FAILED') {
    return t('record.attachment.encryptFailed')
  }
  if (code === 'ABORTED') {
    return t('record.attachment.uploadCancelled')
  }
  return t('record.attachment.uploadFailedRetry')
}

export function urlValidator(value, t) {
  if (value == null || value.length === 0) {
    return Promise.resolve()
  }
  if (value != null && value.length > 0) {
    const urlRegex =
      /^(https?:\/\/)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/g
    const isURL = urlRegex.test(value)

    if (isURL === true) {
      return Promise.resolve()
    }
  }
  return Promise.reject(new Error(t('validation.url.invalid')))
}

export const handleCancelSubmit = ({ openItem, id, disableSave, t, content }) => {
  const goToDetail = () => {
    if (id != null) {
      openItem('detail', id)
    } else {
      openItem(null)
    }
  }

  if (!disableSave) {
    confirmDiscard({
      title: t('record.title.editDiscard'),
      content,
      submit: goToDetail,
    })
  } else if (id != null) {
    openItem('detail', id)
  } else {
    openItem(null)
  }
}

const EditContent = ({
  openItem,
  id,
  messageApi,
  individual,
  reload,
  setProcessing,
  getRsKeyObjFromResource,
  type,
  pageInfo,
}) => {
  const [attachmentLoading, setAttachmentLoading] = useState(false)
  const [disableSave, setDisableSave] = useState(true)
  // 저장 핸들러 재진입 차단 — handleCustomSubmit 주석 참조(라이선스 갱신 await 구간이
  // 무잠금이라 연타 시 레코드가 중복 생성됐다)
  const submittingRef = useRef(false)

  const [fieldManager, setFieldManager] = useState(null)
  const [formInfo, setFormInfo] = useState(null)

  const { t } = useTranslation()
  const ref = useRef()

  const { isLeaked: isLeakedInSettings } = reduxStore.getState().settings

  const antMessage = (text, msgType = 'success') => {
    messageApi.open({
      type: msgType,
      content: text,
    })
  }

  const editSuccessMessage = () =>
    antMessage(t('workspace.record.edit', { service: t(SERVICE_NAME) }))
  const createSuccessMessage = () =>
    antMessage(t('workspace.record.create', { service: t(SERVICE_NAME) }))
  const deleteSuccessMessage = () =>
    antMessage(t('workspace.record.delete', { service: t(SERVICE_NAME) }))

  const closeItem = () => {
    openItem('detail', id)
  }

  const initializeFieldManager = async () => {
    const RsKeyObj = await getRsKeyObjFromResource(individual)

    const attachmentOptions = {
      loadingChanged: (isAttachmentLoading) => {
        setAttachmentLoading(isAttachmentLoading)
      },
      lengthChanged: () => {},
    }
    const onStateChanged = ({ formInfo: newFormInfo, changed }) => {
      console.log(' --- changed : ', changed)
      if (newFormInfo != null) {
        setFormInfo(newFormInfo)
        if (ref.current != null && newFormInfo.initialValues != null) {
          ref.current.setFieldsValue(newFormInfo.initialValues)
        }
      }

      if (type === 'create') {
        setDisableSave(false)
        return
      }

      if (changed != null) {
        setDisableSave(changed !== true)
      }
    }

    const fieldMgr = new FieldManager(
      individual,
      RsKeyObj,
      ref,
      onStateChanged,
      attachmentOptions,
      messageApi,
      individual?.leakedData,
    )
    console.log(fieldMgr)
    setFieldManager(fieldMgr)
  }

  useEffect(() => {
    initializeFieldManager()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [individual])

  const { zeroDarkWebSettings: zeroDarkWebSettingsState } = reduxStore.getState().settings
  const isZeroDarkWebActive = zeroDarkWebSettingsState?.zdw_active === 'true'

  // SUBMIT
  // 3번째 인자를 옵션 객체로 둔 이유: 이 파일의 관례(:105 handleCancelSubmit({ ... }))이고,
  // 구조분해로 같은 이름을 만들면 기존 sendNotification 참조가 무수정으로 살아난다.
  // zdwScan 기본값 'skipped' 는 안전한 방향 — 인자가 누락되면 도장을 찍지 않는다.
  const handleSubmit = async (values, allFields = [], opts = {}) => {
    const { sendNotification = false, zdwScan = 'skipped' } = opts
    setDisableSave(true)
    // 수 분이 걸릴 수 있는 저장·업로드 구간은 자기 잠금을 소유한다.
    // 사전 단계(ZDW 조회)가 잠금을 풀고 넘겨줘도 여기서 다시 잠기므로 편집 유실이 없다.
    // handleCustomSubmit 전체를 try/finally 로 감싸면 안 되는 이유: submit() 이 await 되지 않아
    // 그 finally 가 이 잠금을 곧바로 풀어버린다(설계 §3).
    setProcessing(true)
    disableCreateBtn(true)
    console.log(' --- allFields : ', allFields)

    const updateKeyProps = {
      expired_at: id == null ? new Date().toISOString : values.updated_at,
    }

    // 페이로드 생성(필드 암호화)도 실패할 수 있어 try 안에 둔다 — 밖에 두면 reject 시
    // finally 를 건너뛰어 위에서 건 잠금이 풀리지 않고 폼이 영구 잠긴다.
    let releaseUnloadGuard = null

    try {
      const { files, sendData, chunkedFiles } = await fieldManager.getSendData(updateKeyProps)
      console.log(' ---- send data : ', sendData, JSON.stringify(sendData))

      // 편집일 때만 변경 필드 타입 동봉 — 서버는 암호문 비교가 불가능해(IV 재생성) 프론트가 판정.
      // 신규 생성은 gu_added_record 라 불필요. presigned/legacy 양쪽 다 sendData 를 쓰므로 여기 한 곳이면 된다.
      // 변경 히스토리 전체가 recordChangeHistory flag 하나로 게이팅된다 — OFF 면 재료도 안 보낸다.
      if (id != null && featureFlags.recordChangeHistory) {
        sendData.changed_fields = fieldManager.getChangedFields()
      }

      // 청크 업로드는 수 분이 걸릴 수 있어 이탈 경고를 붙인다. 재개는 지원하지 않으므로 경고만.
      const hasChunkedFiles = chunkedFiles != null && chunkedFiles.length > 0
      releaseUnloadGuard = hasChunkedFiles ? guardUnloadDuringUpload() : null

      let response = null

      // S3 업로드 진행하는 방식
      if (featureFlags.usePresignedUpload) {
        // 편집이면 기존 id, 신규이면서 청크 파일이 있으면 클라이언트가 선발급한다.
        // 신규 + 청크 없음은 기존과 동일하게 서버가 발급하도록 undefined 를 유지한다.
        let targetResourceId = id != null ? individual.id : undefined
        if (hasChunkedFiles && targetResourceId == null) {
          targetResourceId = generateResourceId()
        }

        // 추가된 파일이 있을 때만 presigned URL 받아서 S3 업로드 진행
        if (files.length > 0) {
          const fileMeta = files.map((file) => ({
            filename: file.name,
            content_type: file.type || 'application/octet-stream',
            size: file.size,
          }))

          // 먼저 presigned URL을 받아옴
          const urlResponse = await ResourceService.getUploadUrls(fileMeta, targetResourceId)

          // 그 다음 S3에 파일 업로드
          const { resource_id: presignedResourceId, files: uploadFiles } = urlResponse.data
          const uploadResults = await uploadFilesToS3(files, uploadFiles)
          mapUploadResultsToSendData(sendData, uploadResults)
          // **편집용 upload-urls 는 resource_id 를 되돌려주지 않는다** — 경로에 이미 있으므로
          // 응답 모델에 아예 없다(backend/api/response/models/resource.py:745 "Update용 …
          // resource_id 없음", routers/v1/resource.py:886-891). 그 undefined 로 덮어쓰면 아래
          // 청크 경로가 `/v1/resources/undefined/files/multipart` 를 호출하고, 서버가 그 문자열로
          // object_key 를 조립해 SAFEBOX_852 로 거부한다(2026-08-12 dev QA 실측 — 편집 화면에서
          // 8MiB 이하 파일과 초과 파일을 **함께** 첨부한 경우에만 재현된다).
          // 값이 오는 것은 생성 경로뿐이므로, 있을 때만 서버 확정값으로 통일한다.
          if (presignedResourceId != null) {
            sendData.resource_id = presignedResourceId
            targetResourceId = presignedResourceId
          }
        }

        // 청크 경로: 첨부 시점에 암호화를 건너뛴 파일들을 여기서 스트리밍 암호화·업로드한다.
        if (hasChunkedFiles) {
          const chunkedResults = await runChunkedUploadsSequentially(chunkedFiles, targetResourceId)
          mapUploadResultsToSendData(sendData, chunkedResults)
          // 편집 경로는 URL 의 id 를 쓰므로 body 에 넣지 않는다(기존 동작 유지).
          if (id == null) {
            sendData.resource_id = targetResourceId
          }
        }

        if (id != null) {
          response = await ResourceService.putPresigned({
            id: individual.id,
            data: sendData,
            sendNotification,
          })
        } else {
          response = await ResourceService.createPresigned(sendData)
        }
        // 기존 방식 - 파일이 있을 때는 multipart/form-data로 전송, 없을 때는 application/json으로 전송
      } else {
        const formData = new FormData()
        formData.append('data', JSON.stringify(sendData))
        files.forEach((file) => {
          formData.append('files', file)
        })

        if (id != null) {
          response = await ResourceService.put({
            id: individual.id,
            request: formData,
            sendNotification,
          })
        } else {
          response = await ResourceService.create(formData)
        }
      }

      if (response.code === 200) {
        const resourceId = id != null ? id : response.data.resource_id

        if (isZeroDarkWebActive) {
          if (allFields !== false) {
            const request = {
              resources: [
                {
                  resource_id: resourceId,
                  is_leaked: true,
                  leaked_content: {
                    ...allFields,
                    source: 'ZERODARKWEB',
                  },
                },
              ],
            }

            const bulkPutLeakedResponse = await ResourceService.bulkPutLeaked(request)
            if (bulkPutLeakedResponse.code !== 200) {
              console.error('❌ Error putting leaked data:', bulkPutLeakedResponse.message)
              antMessage(t('workspace.error.failedZeroDarkWeb'), 'error')
              setProcessing(false)
              setDisableSave(false)
              disableCreateBtn(false)
              return
            }
          }
        }

        // 저장이 첨부를 늘리거나 줄였으면 사이드바 용량 게이지(license 의 storage_used /
        // personal_storage_used)가 stale 이 된다 — 그 값은 로그인·워크스페이스 전환에서만
        // 조회되기 때문. 서버는 이 저장에서 capacity 캐시를 무효화했으므로(백엔드
        // invalidate_resource_quota_cache) 지금 재조회하면 새 사용량이 온다.
        // 파일 유무를 따지지 않고 항상 부른다 — 첨부 변경 여부를 프론트에서 정확히 판별하려면
        // add/update/delete 버킷을 모두 봐야 하는데, 한 곳이라도 놓치면 다시 stale 이 된다.
        reduxStore.dispatch({ type: 'user/RELOAD_PAID_LICENSE' })

        if (allFields === false && isLeakedInSettings === true) {
          openItem(null)
        } else {
          const pageOption = id == null ? { removePage: true } : {}
          openItem('detail', resourceId, null, pageOption)
          reduxStore.dispatch({
            type: 'menu/RELOAD',
          })
        }

        // 스캔이 성공했을 때만 "검사 완료" 도장을 찍는다. 도장은 재스캔 게이트
        // (sagas.js:44-52)를 zdw_duration(기본 7일) 동안 억제하므로, 스캔을 안 했거나
        // 실패한 경우에 찍으면 그 기간 다크웹 검사가 통째로 멈춘다.
        if (isZeroDarkWebActive && zdwScan === 'ok') {
          reduxStore.dispatch({
            type: 'settings/SET_SETTINGS',
            payload: {
              zeroDarkWebSettings: {
                zdw_checked_at: new Date().toISOString(),
                zdw_expired_at: getZdwExpiredAt(),
              },
            },
          })
        }

        const tablePage = getTempEnvParam('curTablePage')
        if (id != null) {
          editSuccessMessage()
        } else {
          createSuccessMessage()
        }
        // 조회 catch 지점은 저장 시작 전이라 거기서 띄우면 성공 안내보다 수 분 먼저 떠
        // 따로 사라진다 — 두 사건을 연결하려면 성공 안내 옆이어야 한다.
        // 'error' 가 아니라 'warning': 저장은 성공했고 사용자 행동이 실패한 게 아니다
        // (아래 CHUNK_UPLOAD_ERROR_CODES 분기가 취소를 'warning', 실패를 'error' 로 갈라 쓰는 같은 기준).
        //
        // 상태는 'failed' 인데 문구 키가 skipped 인 이유: 둘이 서로 다른 층을 서술한다.
        // 'failed' 는 **원인**(스캔이 예외로 끝남), skipped 는 **사용자 관점 결과**(검사가
        // 이뤄지지 않음 — 문구도 "하지 못했습니다"). 진짜 'skipped'(이메일 없음)는 실패가
        // 아니라 검사 대상이 없었을 뿐이므로 의도적으로 안내하지 않는다(설계 §8).
        if (zdwScan === 'failed') {
          antMessage(t('workspace.error.skippedZeroDarkWeb'), 'warning')
        }
        if (tablePage) {
          // 생성(id == null)은 기본 정렬(updated_at desc)에서 새 레코드가 맨 앞이라, reload 의
          // target 페이지 탐색이 상세·목록 각 1회를 더 쓰고도 항상 "1페이지"를 돌려준다 →
          // 건너뛴다. 커스텀 정렬 상태면 reload 가 이 옵션을 무시하고 기존 탐색을 태운다.
          // 페이지 리셋은 탐색이 아니라 reload 안의 setPage(1) 가 담당한다 — openItem 의
          // removePage 는 URL page 파라미터만 지우고 pageInfo.offset 은 남기기 때문이다.
          // 수정은 대상이 이미 목록에 있어 reload 가 조기 반환하므로 옵션이 필요 없다.
          tablePage.reload(undefined, id == null ? { skipTargetPageSearch: true } : {})
        }

        setProcessing(false)
      }
    } catch (error) {
      console.error(error)
      if (error != null) {
        // 청크 업로드 실패는 error.code 가 숫자 서버 코드가 아니라 문자열이라
        // (PART_UPLOAD_FAILED 등) errorTranslationMessage 의 코드 표를 타지 못한다 → 전용 안내.
        if (CHUNK_UPLOAD_ERROR_CODES.has(error.code)) {
          // 취소(ABORTED)는 사용자가 의도한 동작 — 빨간 에러 토스트는 "뭔가 잘못됐다"로 읽힌다.
          // 다운로드 취소(Attachment/index.js notifyDownloadFailure 의 message.warning)와 톤을 맞춘다.
          antMessage(
            chunkUploadErrorText(error.code, t),
            error.code === 'ABORTED' ? 'warning' : 'error',
          )
        } else {
          errorTranslationMessage(error.code, t, messageApi)
        }
      }
    } finally {
      if (releaseUnloadGuard != null) {
        releaseUnloadGuard()
      }
      setProcessing(false)
      setDisableSave(false)
      disableCreateBtn(false)
    }
  }
  const { isSendNotification } = useSelector((state) => state.settings)

  // TOTP secret은 체크섬이 없어 형식 이상의 검증이 불가능 — 변경 저장 시 사용자가 사이트 코드와
  // 대조했는지 확인한다(잘못된 키는 동작하던 2단계 인증을 조용히 망가뜨림). PR5.5(#1493).
  // confirmDiscardChanges(utils/ui)와 동일한 Promise<boolean> 패턴.
  const confirmTotpChange = (hasShortKey, duplicateTitle) =>
    new Promise((resolve) => {
      confirmBase({
        icon: 'blue',
        title: t('record.totp.confirmChangeTitle'),
        content: (
          <span>
            {t('record.totp.confirmChangeContent')}
            {hasShortKey ? (
              <React.Fragment>
                <br />
                <br />
                {'⚠ '}
                {t('record.totp.confirmChangeShortKey')}
              </React.Fragment>
            ) : null}
            {duplicateTitle != null ? (
              <React.Fragment>
                <br />
                <br />
                {'⚠ '}
                {t('record.totp.confirmChangeDuplicate', { title: duplicateTitle })}
              </React.Fragment>
            ) : null}
          </span>
        ),
        width: 480,
        okText: t('common.button.yes'),
        cancelText: t('common.button.no'),
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      })
    })

  const handleCustomSubmit = async () => {
    // 재진입 차단(동기) — 아래 라이선스 갱신 await 구간에는 아직 어떤 잠금도 걸리지 않는다
    // (setDisableSave 는 handleSubmit 진입 후, setProcessing 은 그보다 더 뒤). 그래서 왕복
    // 중에 한 번 더 누르면 이 핸들러가 둘 동시 진행되고, 둘 다 재판정을 통과하면 **신규는
    // 레코드 2건 생성 / 편집은 PUT 2회**가 된다 (2026-08-26 검토).
    // state 대신 ref 인 이유: setState 는 리렌더 뒤에야 버튼 disabled 에 반영돼 같은 tick
    // 의 두 번째 클릭을 막지 못한다. 버튼 자체는 계속 활성이어도 되고(두 번째 호출이 여기서
    // 즉시 반환), 조기 반환 경로에서 버튼이 잠긴 채 남는 사고도 생기지 않는다.
    if (submittingRef.current) return
    submittingRef.current = true
    try {
      await runCustomSubmit()
    } finally {
      submittingRef.current = false
    }
  }

  const runCustomSubmit = async () => {
    // 플랜 상한 초과 첨부가 남아 있으면 저장 차단(§4.7 강화 2026-08-25) — 상세의
    // "레코드 수정이 필요합니다" 안내의 실행 수단. 초과 파일을 삭제해야 저장이 풀린다.
    // 판정은 플랜 값(getPlanFileSizeLimit) — 업로드 실효 상한(dev 500MB 확장)과 분리라
    // dev 빌드에서도 Basic(10MB) 초과가 잡힌다. 신규 첨부 fileInfo 도 size 를 가진다.
    const planLimit = getPlanFileSizeLimit()
    if (planLimit != null) {
      const isOversizedNow = () =>
        fieldManager
          .getAllFields()
          .some((field) => field.type === 'files' && hasPlanOversizedFile(field.fileInfos))
      if (isOversizedNow()) {
        // ⚠️ 이 판정은 브라우저가 들고 있는 license 를 쓴다 — 등급이 상향(Basic→PRO)된 뒤
        // 새로고침하지 않은 탭은 옛 상한(10MB)으로 정상 파일까지 막는다. 서버 거부 응답을
        // 신호로 갱신하는 경로(services/jwt PLAN_REJECT_CODES)는 **여기서 서버를 안 부르므로
        // 발동하지 않는다** → 여기서 직접 갱신한다.
        //
        // 갱신을 **기다렸다가 다시 판정**하는 게 핵심이다. dispatch 만 던지고 return 하면
        // (구현 2026-08-25) 첫 클릭은 갱신 트리거로만 소비되고 저장은 실패해, 사용자가
        // "두 번 눌러야 저장된다" 를 겪는다(2026-08-26 사용자 리포트). 새 상한으로 통과하면
        // 같은 클릭에서 저장을 그대로 이어간다.
        await reloadPlanLicense()
        if (isOversizedNow()) {
          antMessage(
            // 갱신된 상한으로 다시 안내 — 옛 값으로 말하면 사용자가 파일을 잘못 줄인다
            t('record.attachment.planLimitBlocksSave', {
              limit: formatBytes(getPlanFileSizeLimit() ?? planLimit, 0),
            }),
            'error',
          )
          return
        }
      }
    }

    const successCheck = fieldManager.preCheckForm()

    try {
      const res = await ref.current.validateFields()

      let zeroDarkWebResponse = null
      let processNewLeakedData = []
      // 'ok' = 스캔 성공 / 'failed' = 스캔 시도했으나 예외 / 'skipped' = 스캔 자체를 안 함
      let zdwScan = 'skipped'

      const allFields = Object.values(fieldManager.getCurrentFormValues())

      setProcessing(true)
      disableCreateBtn(true)

      // successCheck 를 함께 본다 — false 면 preCheckForm 이 이미 검증 표시를 띄웠고
      // 저장은 진행되지 않으므로 외부 API 를 부를 이유가 없다.
      if (successCheck && isZeroDarkWebActive) {
        const requestToZeroDarkWeb = []
        allFields.forEach((item) => {
          if (item) {
            const matches = String(item).match(EMAIL_REGEX)
            if (matches) {
              requestToZeroDarkWeb.push(...matches)
            }
          }
        })

        const uniqueEmails = Array.from(new Set(requestToZeroDarkWeb))
        if (uniqueEmails.length > 0) {
          try {
            zeroDarkWebResponse = await sendDarkWebService(uniqueEmails)
            if (zeroDarkWebResponse.code !== 200) {
              console.error('❌ Error sending DarkWebService:', zeroDarkWebResponse.message)
              // 이 경로는 저장을 중단한다 — 안내가 없으면 사용자가 저장됐다고 착각한다
              antMessage(t('workspace.error.failedZeroDarkWeb'), 'error')
              setProcessing(false)
              disableCreateBtn(false)
              return
            }
            processNewLeakedData = getProcessEmailList(zeroDarkWebResponse.results)
            // 비-200 을 통과한 뒤에만 'ok' — 위 return 경로가 'ok' 로 새면 안 된다
            zdwScan = 'ok'
          } catch (error) {
            console.error('❌ Error sending DarkWebService:', error)
            // 예외를 삼키고 저장은 계속하되(기존 정책) 그 사실을 남긴다
            zdwScan = 'failed'
          }
          // 기존 finally 의 잠금 해제를 제거했다 — 잠금 소유자를 handleSubmit 한 곳으로 모은다.
          // 여기서 풀면 확인 팝업 대기 구간에 폼이 편집 가능해지고, 해제와 재잠금 사이에
          // await 가 끼는 순간 편집 유실이 재발한다(#1864 리뷰 지적).
        }
      }

      const isSentMail =
        individual?.id != null &&
        (individual?.share?.length > 0 || individual?.permission === SRC_TYPE.GROUP_ADMIN)

      // recordFields에서 이메일 형식이면 이메일 객체 안에 아니면 contents 배열 안에 넣는 함수
      const classifyRecordFields = (fields) => {
        const emails = []
        const contents = []
        fields.forEach((field) => {
          if (typeof field === 'string' && EMAIL_REGEX.test(field.trim())) {
            emails.push(field.trim())
          } else if (
            field &&
            typeof field.content === 'string' &&
            EMAIL_REGEX.test(field.content.trim())
          ) {
            emails.push(field.content.trim())
          } else if (field && typeof field.content === 'string') {
            contents.push(field.content.trim())
          } else if (typeof field === 'string') {
            contents.push(field.trim())
          }
        })
        return { email: emails, content: contents }
      }

      const recordFields = allFields.slice(1)
      const recordFieldsClassified = classifyRecordFields(recordFields, processNewLeakedData)
      const isLeaked = checkLeakedRecord(recordFieldsClassified, processNewLeakedData, true)
      const submit = (send) => handleSubmit(res, isLeaked, { sendNotification: send, zdwScan })

      const confirmSendNotification = () => {
        // successCheck 조건 제거 — 이 함수의 호출처 2곳이 모두 if (successCheck) 블록 안이라
        // 항상 true 였다(게이트가 흩어져 생긴 잔재).
        if (isSentMail && isSendNotification) {
          confirmBase({
            icon: 'blue',
            title: t('record.title.notifyOthers'),
            content: (
              <span
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(
                    t('record.desc.notifyOthers', { service: t(SERVICE_NAME) }).replace(
                      /\n/g,
                      '<br />',
                    ),
                  ),
                }}
              />
            ),
            width: 480,
            okText: t('common.button.yes'),
            cancelText: t('common.button.no'),
            onOk: () => submit(true),
            onCancel: () => submit(false),
          })
        } else {
          submit(false)
        }
      }

      if (successCheck) {
        // ?.와 ?? []: RecordEdit.test의 FieldManager mock(brownfield lint 부채 파일이라 무수정)에
        // 메서드가 없어도 안전 — 프로덕션 FieldManager는 항상 메서드를 가진다(설계 §기존테스트영향).
        const changedTotpIds = fieldManager.getChangedTotpFieldIds?.(res) ?? []
        if (changedTotpIds.length > 0) {
          const hasShortKey = changedTotpIds.some((fid) => {
            const len = getTotpSecretLength(res[fid])
            return len != null && len < SHORT_TOTP_SECRET_LEN
          })
          // 중복 경고(#1506) — 세션 캐시(허브 방문분)와만 비교(전수 복호 없음 — 지연 0).
          // 캐시 미방문 상태에서는 미동작(확정 한계 — 허브 배지가 사후 보완).
          // 자기 자신(레코드 편집으로 secret 유지) 지문과의 오탐은 individual.id 제외로 방지.
          const duplicateOf = changedTotpIds
            .map((fid) => hasFingerprint(totpFingerprint(res[fid]), individual?.id))
            .find(Boolean)
          const proceed = await confirmTotpChange(hasShortKey, duplicateOf?.title)
          if (!proceed) {
            // 기존 leaked confirm의 onCancel은 복구를 안 하지만(기존 wart — 보고만),
            // 이 취소 경로는 저장 버튼 잠금을 명시 복구한다
            setProcessing(false)
            disableCreateBtn(false)
            return
          }
        }

        if (isLeaked != null && isLeaked !== false) {
          confirmBase({
            icon: 'blue',
            title: t('dark-web.confirm.title'),
            content: t('dark-web.confirm.description', { service: t(SERVICE_NAME) }),
            width: 480,
            okText: t('common.button.yes'),
            cancelText: t('common.button.no'),
            onOk: confirmSendNotification,
            onCancel: () => {
              // 취소는 저장하지 않고 빠져나가는 경로다 — 잠금을 물고 끝나면 폼이 영구 잠긴다.
              // ESC·마스크 클릭도 onCancel 로 라우팅된다(utils/ui/index.js:829-830 기본 true).
              setProcessing(false)
              disableCreateBtn(false)
            },
          })
          return
        }

        if (individual?.id != null && individual?.is_leaked === true) {
          const request = {
            resources: [{ resource_id: individual.id, is_leaked: false }],
          }

          const response = await ResourceService.bulkPutLeaked(request)

          if (response.code !== 200) {
            console.error('❌ Error putting leaked data:', response.message)
            antMessage(t('workspace.error.failedZeroDarkWeb'), 'error')
            setProcessing(false)
            disableCreateBtn(false)
            return
          }
        }

        confirmSendNotification()
      } else {
        // 필수 첨부 필드가 비어 저장을 시작하지 않는 경로 — 잠금을 물고 끝나면 폼이 영구 잠긴다
        setProcessing(false)
        disableCreateBtn(false)
      }
    } catch (error) {
      console.log(' --- error : ', error)
      setProcessing(false)
      disableCreateBtn(false)
    }
  }

  const handleCancel = () => {
    const goToDetail = () => {
      if (id != null) {
        openItem('detail', id)
      } else {
        openItem(null)
      }
    }
    if (fieldManager.checkChanged()) {
      confirmDiscard({
        title: t('record.title.createDiscard', { service: t(SERVICE_NAME) }),
        content: t('record.desc.createDiscard', { service: t(SERVICE_NAME) }),
        submit: goToDetail,
      })
    } else {
      openItem(null)
    }
  }

  const handleDeleteSubmit = async () => {
    let ret
    setDisableSave(true)
    try {
      ret = await ResourceService.delete({ id: individual.id })
      // 삭제 후엔 상세 패널을 닫고 targetId 를 먼저 정리한다. reload 보다 반드시 먼저 호출해야
      // reload 가 이미 삭제된 레코드의 targetId 를 findTargetRecordPage 로 쫓으며 setPagination 의
      // 페이지 이동과 충돌하는 것을 막는다 (search-table-page-target-id 룰 / PR #1327 패턴).
      // 이 충돌이 "페이지네이션은 9페이지인데 목록은 빈 10페이지" 불일치를 만들었다.
      openItem(null)
      if (ret.code === 200) {
        setPagination(pageInfo)
        reload(deleteSuccessMessage)
        reduxStore.dispatch({
          type: 'menu/RELOAD',
        })
        // 삭제는 용량을 되돌려주므로 게이지도 함께 갱신한다 (저장 경로와 동일 이유)
        reduxStore.dispatch({ type: 'user/RELOAD_PAID_LICENSE' })
      }
      setDisableSave(false)
    } catch (error) {
      if (error != null) {
        errorTranslationMessage(error.code, t, messageApi)
      }
      closeItem()
    }
  }

  const isMobile = useIsMobile()

  const header = (
    <React.Fragment>
      {isMobile ? (
        <div className={styles.mobileHeader}>
          <ArrowLeftIcon onClick={() => openItem(null)} />
          <b>{id === null ? t('chat.button.create') : t('common.button.edit')}</b>
        </div>
      ) : (
        <React.Fragment>
          <b>{id === null ? t('chat.button.create') : t('common.button.edit')}</b>
          <div>
            <Button
              type="text"
              icon={<img src={closeIcon} alt="delete" />}
              onClick={() => (id != null ? handleCancel() : openItem(null))}
            />
          </div>
        </React.Fragment>
      )}
    </React.Fragment>
  )

  const disableSaveBtn = disableSave || attachmentLoading
  const { formItems, formAttrs: formAttrsEx, initialValues } = formInfo || {}
  const formAttrs = {
    className: classNames(styles.formStyle),
    name: 'form',
    layout: 'vertical',
    initialValues,
    validateTrigger: [],
    ref,
    ...formAttrsEx,
  }

  const formElem = (
    <Form {...formAttrs}>
      <div className={classNames(styles.CheckboxContents)}>{formItems}</div>

      <div className={classNames(styles.ButtonContainer)}>
        {id == null ? (
          <React.Fragment>
            <div>{null}</div>
            <div>
              <Button
                type="button"
                className={classNames(styles.CancelButton)}
                onClick={handleCancel}
              >
                {t('common.button.cancel')}
              </Button>
              <Button
                type="primary"
                disabled={disableSaveBtn}
                className={classNames(styles.SaveButton, 'type1')}
                onClick={handleCustomSubmit}
              >
                {t('common.button.save')}
              </Button>
            </div>
          </React.Fragment>
        ) : (
          <React.Fragment>
            {individual != null && individual.permission === SRC_TYPE.RESOURCE_OWNER ? (
              <Button
                type="button"
                className={classNames(styles.DeleteButton, 'delete')}
                onClick={() =>
                  confirmDelete({
                    icon: 'red',
                    title: t('appConfig.title.deleteRecord', { service: t(SERVICE_NAME) }),
                    content: t('appConfig.confirm.deleteRecord', {
                      record: individual.title,
                      service: t(SERVICE_NAME),
                    }),
                    okText: t('common.button.delete'),
                    onOk: handleDeleteSubmit,
                  })
                }
              >
                {t('common.button.delete')}
              </Button>
            ) : (
              <div>{null}</div>
            )}

            <div>
              <Button
                type="submit"
                className={classNames(styles.CancelButton)}
                onClick={() =>
                  handleCancelSubmit({
                    openItem,
                    id,
                    disableSave,
                    t,
                    content: t('record.desc.editDiscard', { service: t(SERVICE_NAME) }),
                  })
                }
              >
                {t('common.button.cancel')}
              </Button>
              <Button
                type="primary"
                disabled={disableSaveBtn}
                className={classNames(styles.SaveButton, 'type1')}
                onClick={handleCustomSubmit}
              >
                {t('common.button.save')}
              </Button>
            </div>
          </React.Fragment>
        )}
      </div>
    </Form>
  )

  return (
    <CardView header={header}>
      <div className={classNames(styles.cardContainer)}>{formElem}</div>
    </CardView>
  )
}

export default EditContent
